import React, { useEffect, useState } from 'react';
import { __ } from '../translations';
import AuthService from './AuthService';

interface AuthCheckerProps {
  checkInterval?: number; // Time in milliseconds between checks
  onSessionExpired?: () => void; // Optional callback for custom handling
}

/**
 * Component that periodically checks if the user is still authenticated
 * and shows a warning or redirects to login page if session has expired
 */
const AuthChecker: React.FC<AuthCheckerProps> = ({
  checkInterval = 5 * 60 * 1000, // Default: check every 5 minutes
  onSessionExpired,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  const checkAuthStatus = async () => {
    try {
      // Use the AuthService to check authentication
      const isAuthenticated = await AuthService.checkAuthentication();
      
      if (!isAuthenticated) {
        setIsAuthenticated(false);
        setShowWarning(true);
        
        if (onSessionExpired) {
          onSessionExpired();
        }
      } else {
        setIsAuthenticated(true);
        setShowWarning(false); // Hide warning if it was showing
      }
    } catch (error) {
      console.error('Error checking authentication status:', error);
    }
  };

  useEffect(() => {
    // Check immediately on component mount
    checkAuthStatus();

    // Set up interval to check periodically
    const intervalId = setInterval(checkAuthStatus, checkInterval);

    // Also check when the window regains focus
    const handleFocus = () => {
      checkAuthStatus();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkInterval, onSessionExpired]);

  const handleReconnect = () => {
    // Use AuthService to redirect to login page
    AuthService.redirectToLogin();
  };

  if (!showWarning) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
        <h2 className="text-xl font-bold mb-4 text-red-600">
          {__('Session Expired')}
        </h2>
        <p className="mb-4">
          {__('Your session has expired. Please log in again to continue.')}
        </p>
        <div className="flex justify-end">
          <button
            onClick={handleReconnect}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            {__('Log In Again')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthChecker;